/*
Wrapper to lazy load Material Table, this allows code splitting and reduces the initial package size.
 */
import React, {Suspense,useState,useRef, useEffect} from 'react';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import {
    HiOutlinePencil,
    HiOutlineTrash,
    HiOutlineCreditCard,
    HiOutlineDocumentText,
    HiOutlineEye,
    HiOutlineDuplicate,
    HiOutlineClipboardList,
    HiOutlineCalendar,
    HiOutlineCheck,
    HiOutlineX,
} from 'react-icons/hi';
import AppProgressBar from '../Progress';
import {TableIcons} from "../index";
import useTheme from '@material-ui/styles/useTheme';


const MaterialTable = React.lazy(() => import('material-table'));

const resolveRowActionIcon = (action, rawIcon) => {
    if (!rawIcon) return null;
    if (action.isFreeAction || action.position === 'toolbar' || action.position === 'toolbarOnSelect') {
        return rawIcon;
    }
    const existingClassName = rawIcon?.props?.className || '';
    if (typeof existingClassName === 'string' && existingClassName.includes('pp-table-action-btn')) {
        return rawIcon;
    }
    // Preserve empty spacer spans (e.g. linesales.jsx) and full Button elements (e.g. Dashboard.jsx)
    if (rawIcon?.type === 'span' && !rawIcon?.props?.children) {
        return rawIcon;
    }
    const tooltipText = typeof action.tooltip === 'string' ? action.tooltip.toLowerCase() : '';
    if (rawIcon?.props?.variant || tooltipText.includes('bulk ')) {
        return rawIcon;
    }

    const childText = typeof rawIcon?.props?.children === 'string'
        ? rawIcon.props.children.trim().toLowerCase()
        : '';
    const iconName = typeof action.icon === 'string' ? action.icon.toLowerCase() : '';

    if (childText === 'delete' || iconName === 'delete' || action.icon === TableIcons.Delete || tooltipText.startsWith('delete') || tooltipText.includes('to delete')) {
        return <span className="pp-table-action-btn pp-table-action-delete"><HiOutlineTrash size={18} /></span>;
    }
    if (childText === 'completed date' || tooltipText.includes('completed')) {
        return <span className="pp-table-action-btn pp-table-action-secondary"><HiOutlineCalendar size={18} /></span>;
    }
    if (childText.startsWith('edit') || iconName === 'edit' || action.icon === TableIcons.Edit || tooltipText.startsWith('edit') || tooltipText.includes('to edit')) {
        return <span className="pp-table-action-btn pp-table-action-edit"><HiOutlinePencil size={18} /></span>;
    }
    if (childText === 'payment' || tooltipText.includes('payment')) {
        return <span className="pp-table-action-btn pp-table-action-secondary"><HiOutlineCreditCard size={18} /></span>;
    }
    if (childText.includes('invoice') || tooltipText.includes('invoice')) {
        return <span className="pp-table-action-btn pp-table-action-secondary"><HiOutlineDocumentText size={18} /></span>;
    }
    if (childText === 'visibility' || tooltipText.startsWith('view')) {
        return <span className="pp-table-action-btn pp-table-action-secondary"><HiOutlineEye size={18} /></span>;
    }
    if (childText.includes('materials') || tooltipText.includes('materials')) {
        return <span className="pp-table-action-btn pp-table-action-secondary"><HiOutlineClipboardList size={18} /></span>;
    }
    if (tooltipText.includes('copy')) {
        return <span className="pp-table-action-btn pp-table-action-secondary"><HiOutlineDuplicate size={18} /></span>;
    }
    if (action.icon === TableIcons.Check || childText === 'check' || tooltipText === 'save') {
        return <span className="pp-table-action-btn pp-table-action-secondary"><HiOutlineCheck size={18} /></span>;
    }
    if (action.icon === TableIcons.Clear || childText === 'clear' || tooltipText === 'cancel') {
        return <span className="pp-table-action-btn pp-table-action-delete"><HiOutlineX size={18} /></span>;
    }

    return <span className="pp-table-action-btn pp-table-action-secondary">{rawIcon}</span>;
};

const TableAction = (props) => {
    let { action, data, disabled: propDisabled, size } = props;
    if (typeof action === 'function') {
        action = action(data);
        if (!action) return null;
    }
    if (action.action) {
        action = action.action(data);
        if (!action) return null;
    }
    if (action.hidden) {
        return null;
    }

    const disabled = action.disabled || propDisabled;
    const handleOnClick = (event) => {
        if (action.onClick) {
            action.onClick(event, data);
            event.stopPropagation();
        }
    };

    const rawIcon = typeof action.icon === 'string'
        ? <Icon {...action.iconProps}>{action.icon}</Icon>
        : typeof action.icon === 'function'
            ? action.icon({ ...action.iconProps, disabled })
            : React.createElement(action.icon, null);

    if (rawIcon === null || rawIcon === undefined || rawIcon === false) {
        return null;
    }

    const icon = resolveRowActionIcon(action, rawIcon);
    const button = (
        <IconButton
            size={size}
            color="inherit"
            disabled={disabled}
            onClick={handleOnClick}
        >
            {icon}
        </IconButton>
    );

    if (action.tooltip) {
        return disabled
            ? <Tooltip title={action.tooltip}><span>{button}</span></Tooltip>
            : <Tooltip title={action.tooltip}>{button}</Tooltip>;
    }
    return button;
};

const Table = (props) => {
    const { options, columns, components, ...rest } = props;
    const {pageSize} = options;
    const [currentPage,setCurrentPage] = useState(0);
    const defaultPageSize = 5
    const [rowsPerPage,setRowsPerPage] = useState(pageSize || defaultPageSize); 
    const tableRef = useRef(null);
    const theme = useTheme();

    const onPageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    }

    const onChangeRowsPerPage = (rows) => {
        setRowsPerPage(rows);
    }
    
    useEffect(() => {
        if (!isNaN(pageSize) && pageSize > 0)
           setRowsPerPage(pageSize);
       
        if (tableRef.current){
            tableRef.current.dataManager.changePageSize(pageSize || defaultPageSize);
                // tableRef.current.onChangePage(null, currentPage);
        }
    }, [pageSize]);

    const renderColumn = (column) => {
        return column?.map((col) => ({
            ...col
        }))
    }
    return <Suspense fallback={<AppProgressBar loading={true}/>}>
                <MaterialTable
                    tableRef={tableRef}
                    icons={
                        TableIcons
                    }
                    components={{
                        Action: TableAction,
                        ...components,
                    }}
                    {...rest}
                    columns={renderColumn(columns)}
                    options={{
                            ...options,
                            initialPage:currentPage,
                            pageSize:rowsPerPage,
                            headerStyle: {
                                backgroundColor: theme.palette.primary.secondary,
                                color: theme.palette.text.default
                            },
                            rowStyle: rowData => {
                                if(rowData?.rowType === 'Footer'){
                                    return {fontWeight: 'bold'}
                                }
                                return {}
                            }
                        }
                    }
                    onPageChange={onPageChange}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                />
            </Suspense>
};

export default Table;
//export default Table;
